(function () {
  'use strict';

  function buildPlan(cart, config, preferredId) {
    const items = cart.items || [];
    let bundles = 0;
    let singles = 0;
    let subscriptions = 0;

    items.forEach(item => {
      const id = Number(item.variant_id || item.id);

      if (id === config.bundleId) {
        bundles += item.quantity;
      } else if (config.singleIds.includes(id)) {
        if (item.selling_plan_allocation) {
          subscriptions += item.quantity;
        } else {
          singles += item.quantity;
        }
      }
    });

    const qualifying = bundles + singles + subscriptions > 0;

    const existingWrist = items.find(item =>
      [config.blackId, config.whiteId].includes(
        Number(item.variant_id || item.id)
      )
    );

    const savedColour =
      cart.attributes && cart.attributes['TRU wristband colour'];

    let selected =
      Number(preferredId) ||
      (existingWrist &&
        Number(existingWrist.variant_id || existingWrist.id)) ||
      (savedColour === 'White'
        ? config.whiteId
        : savedColour === 'Black'
          ? config.blackId
          : 0);

    const available = id =>
      id === config.blackId
        ? config.blackAvailable
        : id === config.whiteId
          ? config.whiteAvailable
          : false;

    if (!available(selected)) {
      selected = config.blackAvailable
        ? config.blackId
        : config.whiteAvailable
          ? config.whiteId
          : 0;
    }

    const targets = new Map(
      [
        [config.legacyId, 0],
        [
          config.bottleId,
          bundles +
          (singles >= 2 ? 1 : 0) +
          (subscriptions > 0 ? 1 : 0)
        ],
        [
          config.blackId,
          qualifying && selected === config.blackId ? 1 : 0
        ],
        [
          config.whiteId,
          qualifying && selected === config.whiteId ? 1 : 0
        ],
        [
          config.stickerId,
          qualifying && config.stickerAvailable ? 1 : 0
        ]
      ].filter(entry => entry[0])
    );

    const updates = {};
    const additions = [];

    for (const [id, target] of targets) {
      const lines = items.filter(
        item => Number(item.variant_id || item.id) === id
      );

      lines.forEach((line, index) => {
        const desired = index === 0 ? target : 0;
        if (line.quantity !== desired) {
          updates[line.key] = desired;
        }
      });

      if (target > 0 && !lines.length) {
        additions.push({
          id,
          quantity: target,
          properties: {
            '_TRU gift': 'Included with qualifying purchase'
          }
        });
      }
    }

    return {
      qualifying,
      selected: qualifying ? selected : 0,
      updates,
      additions
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { buildPlan };
  }

  if (
    typeof window === 'undefined' ||
    typeof document === 'undefined'
  ) {
    return;
  }

  if (window.TruCartGifts) return;

  const node = document.getElementById('tru-cart-gift-config');
  if (!node) return;

  let config;

  try {
    config = JSON.parse(node.textContent);
  } catch (_) {
    return;
  }

  if (!config.enabled) {
    const explain = () => {
      document.querySelectorAll('[data-tru-gift-status]')
        .forEach(element => {
          element.textContent =
            'Gift selection is temporarily unavailable.';
        });
    };

    document.addEventListener('DOMContentLoaded', explain);
    explain();
    return;
  }

  const root = config.root.endsWith('/')
    ? config.root
    : config.root + '/';

  let queue = Promise.resolve();
  let busy = 0;
  let lastFailure = '';
  let statusText = '';

  const failureStorageKey = 'tru-cart-gift-discount-failure';

  try {
    lastFailure = sessionStorage.getItem(failureStorageKey) || '';
  } catch (_) {}

  function rememberFailure(value) {
    lastFailure = value;

    try {
      if (value) {
        sessionStorage.setItem(failureStorageKey, value);
      } else {
        sessionStorage.removeItem(failureStorageKey);
      }
    } catch (_) {}
  }

  const checkoutStates = new Map();

  function setBusy(value) {
    document.querySelectorAll('tru-cart-gifts').forEach(element => {
      element.setAttribute('aria-busy', String(value));
    });

    document.querySelectorAll(
      'button[name="checkout"], input[name="checkout"]'
    ).forEach(button => {
      if (value && !checkoutStates.has(button)) {
        checkoutStates.set(button, button.disabled);
        button.disabled = true;
      }
    });

    if (!value) {
      checkoutStates.forEach((disabled, button) => {
        if (button.isConnected) button.disabled = disabled;
      });
      checkoutStates.clear();
    }
  }

  function status(message) {
    statusText = message;

    document.querySelectorAll('[data-tru-gift-status]')
      .forEach(element => {
        element.textContent = message;
      });
  }

  async function request(path, data) {
    const response = await fetch(
      root + path,
      data
        ? {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify(data)
          }
        : {
            headers: { Accept: 'application/json' },
            cache: 'no-store'
          }
    );

    const result = await response.json();

    if (!response.ok) {
      throw new Error(
        result.description ||
        result.message ||
        'Unable to update your gift. Please try again.'
      );
    }

    return result;
  }

  function signature(cart) {
    return JSON.stringify(
      (cart.items || [])
        .filter(item =>
          config.singleIds.includes(
            Number(item.variant_id || item.id)
          ) ||
          Number(item.variant_id || item.id) === config.bundleId
        )
        .map(item => [
          item.key,
          item.quantity,
          item.final_line_price
        ])
    );
  }

  function sectionIds() {
    const sections = [];

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections }
      })
    );

    return [...new Set(sections)].slice(0, 5);
  }

  function sectionPayload() {
    return {
      sections: sectionIds(),
      sections_url: window.location.pathname
    };
  }

  async function getSections(cart) {
    const unique = sectionIds();

    if (!unique.length) {
      cart.sections = {};
      return cart;
    }

    if (
      cart.sections &&
      unique.every(id => cart.sections[id])
    ) {
      return cart;
    }

    const url = new URL(window.location.href);
    url.searchParams.set('sections', unique.join(','));

    const response = await fetch(url.pathname + url.search, {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(
        'Cart updated. Please refresh to see your selection.'
      );
    }

    const sections = await response.json();

    if (unique.some(id => !sections[id])) {
      throw new Error(
        'Cart updated. Please refresh to see your selection.'
      );
    }

    cart.sections = sections;
    return cart;
  }

  function publishCart(cart) {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          cart,
          baseEvent: 'variant:add',
          truGiftSync: true
        }
      })
    );
  }

  function onCartPage() {
    return window.location.pathname
      .replace(/\/$/, '')
      .endsWith('/cart');
  }

  async function synchronize(options) {
    const cartBefore = await request('cart.js');
    const plan = buildPlan(cartBefore, config, options.preferredId);

    if (
      options.preferredId &&
      Number(options.preferredId) !== plan.selected &&
      plan.qualifying
    ) {
      throw new Error(
        'That wristband is unavailable. Please choose the other colour.'
      );
    }

    const failureKey = signature(cartBefore);

    if (!options.preferredId && lastFailure === failureKey) {
      status(
        'Your gift could not be added. Please contact us if it remains unavailable.'
      );
      return getSections(cartBefore);
    }

    let cart = cartBefore;
    let changed = false;

    if (Object.keys(plan.updates).length) {
      cart = await request('cart/update.js', {
        updates: plan.updates
      });
      changed = true;
    }

    if (plan.additions.length) {
      try {
        await request('cart/add.js', {
          items: plan.additions
        });
        changed = true;
      } catch (error) {
        cart = await request('cart.js');
        rememberFailure(signature(cart));

        if (options.publish !== false) {
          await getSections(cart);
          publishCart(cart);
        }

        if (
          changed &&
          options.publish !== false &&
          onCartPage()
        ) {
          window.location.reload();
        }

        throw error;
      }

      cart = await request('cart/update.js', {
        attributes: {
          'TRU wristband colour':
            plan.selected === config.whiteId
              ? 'White'
              : plan.selected === config.blackId
                ? 'Black'
                : ''
        },
        ...sectionPayload()
      });
    }

    const paidGifts = (cart.items || []).filter(item =>
      [config.blackId, config.whiteId, config.stickerId]
        .includes(Number(item.variant_id || item.id)) &&
      item.final_line_price > 0
    );

    if (paidGifts.length) {
      const updates = {};

      paidGifts.forEach(item => {
        updates[item.key] = 0;
      });

      cart = await request('cart/update.js', { updates });
      changed = true;
      rememberFailure(signature(cart));

      status(
        'A gift discount did not apply. The affected gift was removed so you are not charged. Please contact us.'
      );
    } else {
      rememberFailure('');
      status('');
    }

    const wrist = (cart.items || []).find(item =>
      [config.blackId, config.whiteId].includes(
        Number(item.variant_id || item.id)
      )
    );

    const colour = wrist
      ? Number(wrist.variant_id || wrist.id) === config.blackId
        ? 'Black'
        : 'White'
      : '';

    if (
      ((cart.attributes &&
        cart.attributes['TRU wristband colour']) || '') !== colour
    ) {
      cart = await request('cart/update.js', {
        attributes: {
          'TRU wristband colour': colour
        },
        ...sectionPayload()
      });
      changed = true;
    }

    await getSections(cart);

    if (options.publish !== false && changed) {
      publishCart(cart);

      if (onCartPage()) {
        window.location.reload();
      }
    }

    return cart;
  }

  function run(options = {}) {
    busy += 1;
    setBusy(true);

    const operation = queue
      .catch(() => {})
      .then(() => synchronize(options));

    queue = operation;

    return operation
      .catch(error => {
        status(
          error.message ||
          'Unable to update your gift. Please try again.'
        );
        throw error;
      })
      .finally(() => {
        busy -= 1;
        if (!busy) setBusy(false);
      });
  }

  window.TruCartGifts = { sync: run };

  if (!customElements.get('tru-cart-gifts')) {
    customElements.define(
      'tru-cart-gifts',
      class extends HTMLElement {
        connectedCallback() {
          this.setAttribute('aria-busy', String(busy > 0));

          const message = this.querySelector('[data-tru-gift-status]');
          if (message) message.textContent = statusText;
        }
      }
    );
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-tru-wristband]');
    if (!button || button.disabled || busy) return;

    const container = button.closest('tru-cart-gifts');
    if (!container) return;

    const choices = [
      ...container.querySelectorAll('[data-tru-wristband]')
    ];

    const previous = choices.map(choice =>
      choice.getAttribute('aria-pressed')
    );

    choices.forEach(choice => {
      choice.setAttribute('aria-pressed', String(choice === button));
    });

    status('Updating wristband…');

    run({
      preferredId: Number(button.dataset.truWristband)
    }).catch(() => {
      choices.forEach((choice, index) => {
        if (choice.isConnected) {
          choice.setAttribute('aria-pressed', previous[index]);
        }
      });
    });
  });

  document.addEventListener('cart:change', event => {
    if (!event.detail?.truGiftSync) {
      run().catch(() => {});
    }
  });

  window.addEventListener('pageshow', event => {
    if (event.persisted) run().catch(() => {});
  });

  customElements
    .whenDefined('cart-drawer')
    .then(() => run())
    .catch(() => {});
})();